<script setup lang="ts">
type Props = {
  notification: TInboxMessage;
};

const { notification } = defineProps<Props>();

const { runAction } = useSmarticoInit();
const { openChangeNickname, openChangeAvatar } = useSmarticoUserInfos();

const { loadSingleMessage, markNotificationAsRead, loadMessages } =
  useSmarticoMessages();

const isReadManual = ref<boolean>(false);

const isNotificationRead = computed(() => {
  return notification.read || isReadManual.value;
});

const titleCircleColor = computed(() => {
  return isNotificationRead.value ? 'titleCircleRead' : 'titleCircleUnread';
});

const isLoadingItem = ref<boolean>(false);
const currentNotification = ref<TInboxMessageBody>();

const loadNotificationDetails = async () => {
  isLoadingItem.value = true;

  currentNotification.value = await loadSingleMessage(
    notification.message_guid,
  );

  setTimeout(() => {
    isLoadingItem.value = false;
  }, 300);
};

const markAsRead = async () => {
  if (!notification.read) {
    await markNotificationAsRead(notification.message_guid);
    isReadManual.value = true;
    loadMessages();
  }
};

const computedMessageBody = computed(() => {
  if (currentNotification.value?.html_body) {
    return currentNotification.value?.html_body;
  } else if (currentNotification.value?.body) {
    return currentNotification.value?.body;
  } else if (currentNotification.value?.preview_body) {
    return currentNotification.value?.preview_body;
  } else {
    return '';
  }
});

const actionButtonText = computed(() => {
  const text = ref('Abrir');
  const action = currentNotification.value?.action;

  if (action?.includes('missions')) {
    text.value = 'Acessar Missões';
  } else if (action?.includes('levels')) {
    text.value = 'Acessar Níveis';
  } else if (action?.includes('tournaments')) {
    text.value = 'Acessar Torneios';
  } else if (action?.includes('store')) {
    text.value = 'Acessar Loja';
  } else if (action?.includes('push')) {
    text.value = 'Liberar Notificações Push';
  } else if (action?.includes('nickname')) {
    text.value = 'Alterar Nickname';
  } else if (action?.includes('avatar')) {
    text.value = 'Alterar Avatar';
  }

  return text.value;
});

const extractUrlFromAction = (
  action: string,
  prefix: string,
): string | null => {
  if (action.startsWith(prefix)) {
    const parts = action.split(prefix);

    return parts[1] || null;
  }

  return null;
};

const navigateActions = {
  openExternal: (url: string) => {
    navigateTo(url, { open: { target: '_blank' } });
  },
  openInternal: (pathSuffix: string) => {
    navigateTo(localeLink(`/vip/${pathSuffix}`), {
      open: { target: '_blank' },
    });
  },
};

const actionHandlers = [
  {
    check: (act: string) => act.startsWith('dp:go&url='),
    handle: (act: string) => {
      const url = extractUrlFromAction(act, 'dp:go&url=');

      if (url) {
        navigateActions.openExternal(url);
      } else {
        console.error('URL is null, cannot navigate');
      }
    },
  },
  {
    check: (act: string) => /^https:\/\//.test(act),
    handle: (act: string) => navigateActions.openExternal(act),
  },
  {
    check: (act: string) =>
      ['missions', 'levels', 'tournaments', 'store'].some((key) =>
        act.includes(key),
      ),
    handle: (act: string) => {
      const pathSuffix =
        ['missions', 'levels', 'tournaments', 'store'].find((key) =>
          act.includes(key),
        ) || '';

      navigateActions.openInternal(pathSuffix);
    },
  },
  {
    check: (act: string) => act.includes('nickname'),
    handle: () => openChangeNickname(),
  },
  {
    check: (act: string) => act.includes('avatar'),
    handle: () => openChangeAvatar(),
  },
];

const actionNotification = (guid: string, act: string = 'dp:ok') => {
  markNotificationAsRead(guid);

  // Process the action using handlers
  const handler = actionHandlers.find(({ check }) => check(act));

  if (handler) {
    handler.handle(act);
  } else {
    runAction(act);
  }

  loadMessages();
  loadNotificationDetails();
  isReadManual.value = true;
};

onMounted(async () => {
  await loadNotificationDetails();
});
</script>

<template>
  <div class="defaultItemBox notHover">
    <template v-if="isLoadingItem">
      <div :class="$style.loadingItem">
        <div
          v-for="(n, index) in 3"
          :key="`skeleton-${index}`"
          class="load skeleton-loading"
          :class="$style.skeleton"
        ></div>
      </div>
    </template>
    <template v-else>
      <div
        :class="[
          `defaultBoxContent notificationBox`,
          { [`isRead`]: isNotificationRead },
        ]"
      >
        <div class="titleBox smaller">
          <span
            :class="[titleCircleColor, 'cursor-pointer']"
            title="Marcar como lida"
            @click="markAsRead"
          ></span>
          {{ currentNotification?.title }}
        </div>

        <div
          class="descriptionBox"
          v-html="computedMessageBody"
        />
        <div class="infoBox">
          <div class="itemInfo">
            <div class="actionBox">
              <button
                v-if="
                  currentNotification?.action &&
                  currentNotification?.action !== 'dp:ok'
                "
                class="items-center justify-center inline-flex gap-1.5"
                @click="
                  actionNotification(
                    notification.message_guid,
                    currentNotification?.action,
                  )
                "
              >
                <NuxtIcon
                  class="text-sm"
                  name="open"
                />
                {{ actionButtonText }}
              </button>

              <button
                v-else-if="
                  currentNotification?.action &&
                  currentNotification?.action === 'dp:ok' &&
                  !isNotificationRead
                "
                @click="
                  actionNotification(
                    notification.message_guid,
                    currentNotification?.action,
                  )
                "
              >
                <span>{{ $t('gamification.button.mark_as_read') }}</span>
              </button>
              <button
                v-else
                class="inactive"
              >
                <span>{{ $t('gamification.button.already_viewed') }}</span>
              </button>
            </div>
          </div>
          <div class="itemInfo m-0">
            <span>{{ notification.sent_date.split(' ')[0] }}</span>
          </div>
        </div>
      </div>
      <pre
        v-if="isDev() && false"
        class="gamificationPreDebug"
        style="height: 100px"
      >
      {{ notification }}
      {{ currentNotification }}
    </pre
      >
    </template>
  </div>
</template>

<style module lang="postcss">
.loadingItem {
  @apply flex gap-3 flex-wrap p-3;
  .skeleton {
    @apply invert opacity-10 w-full;
    &:nth-child(1) {
      @apply h-[25px];
    }
    &:nth-child(2) {
      @apply h-[60px];
    }
    &:nth-child(3) {
      @apply h-[20px];
    }
  }
}
</style>
